import UserProfile from "@/domain/entities/user/UserProfile";
import EmailDisplayMode from "@/domain/enums/EmailDisplayMode";
import { queryKeys } from "@/lib/queryKeys";
import { userToSDKMapper } from "@/mappers/user/userMapper";
import {
    EmailDisplayMode as EmailDisplayModeSDK,
    useSdk,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import useGetUserProfile from "../useGetUserProfile";
import useErrorToast from "../utils/useErrorToast";

const useSetDisplayModeSetting = () => {
    const { userManager } = useSdk();
    const showToast = useErrorToast();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: user } = useGetUserProfile();

    const setDisplayModeMutation = useMutation({
        mutationFn: async (displayMode: EmailDisplayMode): Promise<void> => {
            if (!user) {
                throw new Error("User is not available");
            }

            await userManager.setUserEmailDisplayMode(
                displayMode as EmailDisplayModeSDK,
            );
        },
        onMutate: async (displayMode: EmailDisplayMode) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.user.all() });

            const previousUser = queryClient.getQueryData(
                queryKeys.user.profile(),
            );

            if (user) {
                const newUser = new UserProfile({
                    ...user,
                    settings: {
                        ...user.settings,
                        displayPreferences: {
                            ...user.settings.displayPreferences,
                            emailDisplayMode: displayMode,
                        },
                    },
                });

                const newUserSDK = userToSDKMapper(newUser);
                queryClient.setQueryData(queryKeys.user.profile(), newUserSDK);
            }

            return { previousUser };
        },
        onError: (err, _, context) => {
            console.error("Error setting display mode", err);

            showToast({
                titleTranslationKey: t("errors.displayModeSettings"),
            });

            if (context?.previousUser) {
                queryClient.setQueryData(
                    queryKeys.user.profile(),
                    context.previousUser,
                );
            } else {
                queryClient.invalidateQueries({
                    queryKey: queryKeys.user.all(),
                });
            }
        },
    });

    return {
        setDisplayMode: setDisplayModeMutation.mutateAsync,
        isLoading: setDisplayModeMutation.isPending,
        error: setDisplayModeMutation.error,
    };
};

export default useSetDisplayModeSetting;
